import type { TFunction } from 'i18next';

import type { WarningMetric } from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { FEEDING_COLORS, type WarningPalette } from './feedingChartColors';
import { formatTarget } from './PenComparisonTable/penComparisonFormat';
import { formatSettingPercent } from './settingPercent';

/** What a key entry draws before its text: a bar's fill, or a reference line's dashes. */
export type KeySwatch =
    | { kind: 'fill'; color: string; outline: string }
    | { kind: 'line'; colors: string[] };

export interface KeyEntry {
    id: string;
    label: string;
    /** Absent on an entry about the bars' order rather than a colour. */
    swatch?: KeySwatch;
}

export interface TargetKeyInput {
    /** The site target the dashed line is drawn at. */
    target: number;
    /** Which target the line is, so the key names it as the cards and the table do. */
    metric: WarningMetric;
    lineColor: string;
    /**
     * Set while a bar is warned: the tier swatches and, when each pen has both bars
     * (`paired`), which bar is which series.
     */
    warned?: { palette: WarningPalette; systemName: string; paired: boolean };
}

/**
 * The per-pen bFCR and RGI key: the site target always, since its line has no label in
 * the plot; the warning tiers and the bar order only while a warned bar has lost its series
 * colour.
 */
export function targetKeyEntries(input: TargetKeyInput, t: TFunction, locale: Locale): KeyEntry[] {
    const { target, metric, lineColor, warned } = input;
    const value = formatTarget(target, metric, locale);
    const entries: KeyEntry[] = [
        {
            id: 'target',
            label: t('feedingModule.charts.targetKey', { value }),
            swatch: { kind: 'line', colors: [lineColor] },
        },
    ];
    if (!warned) return entries;
    const { palette, systemName, paired } = warned;
    const tiers: KeyEntry[] = [
        ...entries,
        {
            id: 'warning',
            label: t('feedingModule.charts.warningKeyWarning'),
            swatch: { kind: 'fill', color: palette.warning, outline: palette.outline },
        },
        {
            id: 'critical',
            label: t('feedingModule.charts.warningKeyCritical'),
            swatch: { kind: 'fill', color: palette.critical, outline: palette.outline },
        },
    ];
    // With one series hidden for lack of values, "left" and "right" would name a bar that
    // is not drawn.
    if (!paired) return tiers;
    return [
        ...tiers,
        { id: 'order', label: t('feedingModule.charts.warningKeyOrder', { system: systemName }) },
    ];
}

export interface PelletKeyInput {
    /** The site thresholds the two dashed lines are drawn at. */
    warnPct: number;
    alertPct: number;
    palette: WarningPalette;
    /** Whether any bar takes a band's colour; the band swatches show only then. */
    banded: boolean;
}

/**
 * The pellet key, in the warning colours the bars take. The swatches carry no numbers: a
 * per-pen chart colours each bar by its own pen's thresholds, which the site values would
 * misstate, so the site values are named once, with the lines they are drawn as.
 */
export function pelletKeyEntries(input: PelletKeyInput, t: TFunction, locale: Locale): KeyEntry[] {
    const { warnPct, alertPct, palette, banded } = input;
    const fill = (color: string, outline = color): KeySwatch => ({ kind: 'fill', color, outline });
    const thresholds: KeyEntry = {
        id: 'thresholds',
        label: t('feedingModule.charts.pelletThresholdKey', {
            warn: formatSettingPercent(warnPct, locale),
            alert: formatSettingPercent(alertPct, locale),
        }),
        swatch: { kind: 'line', colors: [palette.warning, palette.critical] },
    };
    if (!banded) return [thresholds];
    return [
        {
            id: 'none',
            label: t('feedingModule.charts.pelletKeyNone'),
            swatch: fill(FEEDING_COLORS.bioscope),
        },
        {
            id: 'elevated',
            label: t('feedingModule.charts.pelletKeyElevated'),
            swatch: fill(palette.warning, palette.outline),
        },
        {
            id: 'high',
            label: t('feedingModule.charts.pelletKeyHigh'),
            swatch: fill(palette.critical, palette.outline),
        },
        thresholds,
    ];
}
